import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FiFolder, FiSearch, FiPlus, FiTrash2 } from 'react-icons/fi';
import * as categoryService from '../../services/categories';
import '../../styles/main.scss';

const Categories = () => {
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState({
    search: '',
    includeInactive: false,
  });

  const [formData, setFormData] = useState({
    name: '',
    description: '',
  });

  const {
    data: categoriesResponse,
    isLoading,
    isError,
    refetch,
  } = useQuery(
    ['categories', filters],
    () =>
      categoryService.getCategories({
        search: filters.search || undefined,
        includeInactive: filters.includeInactive ? true : undefined,
      })
  );

  const categories =
    categoriesResponse?.data?.data ||
    categoriesResponse?.data ||
    categoriesResponse?.categories ||
    [];

  const createCategoryMutation = useMutation(
    (payload) => categoryService.createCategory(payload),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('categories');
        setFormData({
          name: '',
          description: '',
        });
      },
    }
  );

  const deleteCategoryMutation = useMutation(
    (id) => categoryService.deleteCategory(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('categories');
      },
    }
  );

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleCreateCategory = (event) => {
    event.preventDefault();
    if (!formData.name) {
      return;
    }
    createCategoryMutation.mutate(formData);
  };

  const handleDeleteCategory = (category) => {
    if (!window.confirm(`Delete category "${category.name}"?`)) {
      return;
    }
    deleteCategoryMutation.mutate(category._id);
  };

  return (
    <div className="dashboard-page categories-page">
      <div className="page-header">
        <div>
          <h1>Categories</h1>
          <p>Manage document categories and hierarchy</p>
        </div>
        <button
          type="button"
          className="btn btn-outline"
          onClick={() => refetch()}
          disabled={isLoading}
        >
          Refresh
        </button>
      </div>

      <div className="page-content">
        <div className="users-layout">
          <div className="users-main">
            <div className="users-filters">
              <div className="search-box">
                <FiSearch className="search-icon" />
                <input
                  type="text"
                  placeholder="Search categories by name or description..."
                  value={filters.search}
                  onChange={(event) =>
                    handleFilterChange('search', event.target.value)
                  }
                />
              </div>
              <label className="checkbox-inline">
                <input
                  type="checkbox"
                  checked={filters.includeInactive}
                  onChange={(event) =>
                    handleFilterChange('includeInactive', event.target.checked)
                  }
                />
                <span>Show inactive</span>
              </label>
            </div>

            <div className="users-table-wrapper">
              {isLoading ? (
                <div className="loading-placeholder">
                  {[...Array(5)].map((_, index) => (
                    <div key={index} className="skeleton-row" />
                  ))}
                </div>
              ) : isError ? (
                <div className="error-state">
                  <p>Failed to load categories.</p>
                </div>
              ) : categories.length === 0 ? (
                <div className="empty-state">
                  <FiFolder size={32} />
                  <p>No categories found</p>
                </div>
              ) : (
                <table className="table users-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Description</th>
                      <th>Parent</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.map((category) => (
                      <motion.tr
                        key={category._id}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                      >
                        <td>
                          <div className="user-cell">
                            <div className="user-avatar">
                              <FiFolder />
                            </div>
                            <div className="user-info">
                              <div className="user-name">
                                <Link to={`/categories/${category._id}`}>
                                  {category.name}
                                </Link>
                              </div>
                            </div>
                          </div>
                        </td>
                        <td>{category.description || '-'}</td>
                        <td>{category.parentCategoryId?.name || '-'}</td>
                        <td>
                          <span
                            className={
                              category.isActive
                                ? 'badge badge-success'
                                : 'badge badge-outline'
                            }
                          >
                            {category.isActive ? 'Active' : 'Inactive'}
                          </span>
                        </td>
                        <td>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => handleDeleteCategory(category)}
                            disabled={deleteCategoryMutation.isLoading}
                          >
                            <FiTrash2 />
                          </button>
                        </td>
                      </motion.tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>

          <div className="users-sidebar">
            <div className="card">
              <h3>Create category</h3>
              <form onSubmit={handleCreateCategory}>
                <div className="form-group">
                  <label>Name</label>
                  <input
                    type="text"
                    className="form-control"
                    value={formData.name}
                    onChange={(event) =>
                      setFormData((prev) => ({
                        ...prev,
                        name: event.target.value,
                      }))
                    }
                  />
                </div>
                <div className="form-group">
                  <label>Description</label>
                  <textarea
                    className="form-control"
                    rows={3}
                    value={formData.description}
                    onChange={(event) =>
                      setFormData((prev) => ({
                        ...prev,
                        description: event.target.value,
                      }))
                    }
                  />
                </div>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={createCategoryMutation.isLoading}
                >
                  <FiPlus />
                  {createCategoryMutation.isLoading
                    ? 'Creating...'
                    : 'Create category'}
                </button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Categories;
